/**
 * Delete Facility Confirmation Modal
 * Single responsibility: present confirmation and delegate onConfirm/onClose to parent.
 */

import React from 'react';
import { X, AlertTriangle } from 'lucide-react';
import { formatFacilityAddress } from './utils/facilityDisplayUtils';

/**
 * @typedef {Object} Facility
 * @property {string} id
 * @property {string} name
 * @property {string} address
 */

/**
 * @typedef {Object} DeleteFacilityModalProps
 * @property {boolean} isOpen
 * @property {() => void} onClose
 * @property {() => void} onConfirm
 * @property {Facility} facility
 * @property {number} [locationsCount]
 * @property {boolean} [hasCleaningStandards]
 * @property {boolean} [hasSoilLoadLevels]
 * @property {boolean} [hasWaterChanges]
 */

export const DeleteFacilityModal = ({
  isOpen,
  onClose,
  onConfirm,
  facility,
  locationsCount = 0,
  hasCleaningStandards = false,
  hasSoilLoadLevels = false,
  hasWaterChanges = false,
}) => {
  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  if (!isOpen) return null;

  const hasAnyData =
    locationsCount > 0 || hasCleaningStandards || hasSoilLoadLevels || hasWaterChanges;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-[rgba(184,182,182,0.5)] z-50" onClick={onClose} />

      {/* Modal */}
      <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-[10px] px-8 py-8 w-[90vw] max-w-[540px] z-[60] shadow-2xl">
        <div className="flex flex-col gap-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <h2 className="font-['Roboto:Bold',sans-serif] font-bold text-[24px] text-black">
                Delete Facility
              </h2>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-[#F2F4F8] hover:bg-gray-300 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5 text-[#1C1B1F]" />
            </button>
          </div>

          {/* Facility Name - shared info card style */}
          <div className="rounded-2xl border border-[#A5D8FF] bg-[#E8F4FC] p-4">
            <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[16px] text-[#333C4D]">
              {facility?.name}
            </p>
            <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-[#333C4D] mt-1 leading-relaxed">
              {formatFacilityAddress(facility)}
            </p>
          </div>

          {/* Warning Message */}
          <div className="space-y-3">
            <p className="font-['Roboto:Medium',sans-serif] text-[15px] text-gray-800 leading-relaxed">
              Are you sure? This will delete{' '}
              <span className="font-['Roboto:Bold',sans-serif] font-bold text-gray-900">
                {facility?.name}
              </span>
              {locationsCount > 0 && (
                <>
                  {' '}and all{' '}
                  <span className="font-['Roboto:Bold',sans-serif] font-bold text-gray-900">
                    {locationsCount}
                  </span>{' '}
                  location{locationsCount !== 1 ? 's' : ''}
                </>
              )}{' '}
              permanently.
            </p>

            {hasAnyData && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <p className="font-['Roboto:SemiBold',sans-serif] font-semibold text-[14px] text-amber-900 mb-3">
                  The following will also be deleted:
                </p>

                <div className="space-y-2 ml-2">
                  {locationsCount > 0 && (
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-amber-800">
                        <span className="font-['Roboto:SemiBold',sans-serif] font-semibold">
                          {locationsCount}
                        </span>{' '}
                        location{locationsCount !== 1 ? 's' : ''}
                      </p>
                    </div>
                  )}

                  {hasCleaningStandards && (
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-amber-800">
                        Cleaning Standards configuration
                      </p>
                    </div>
                  )}

                  {hasSoilLoadLevels && (
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-amber-800">
                        Soil Load Levels configuration
                      </p>
                    </div>
                  )}

                  {hasWaterChanges && (
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-amber-600" />
                      <p className="font-['Roboto:Regular',sans-serif] text-[14px] text-amber-800">
                        Water Changes configuration
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            <p className="font-['Roboto:Medium',sans-serif] text-[14px] text-gray-600 leading-relaxed flex items-center gap-1.5">
              <AlertTriangle className="w-4 h-4 shrink-0" aria-hidden />
              This action cannot be undone.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="border-2 border-gray-300 bg-white text-gray-700 rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>

            <button
              type="button"
              onClick={handleConfirm}
              className="bg-red-600 text-white rounded-lg px-6 py-2.5 font-['Roboto:SemiBold',sans-serif] font-semibold text-[15px] hover:bg-red-700 transition-colors"
            >
              Delete Facility
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
